import {
  OptimizationJson,
  ExperienceOptimizationJson,
  AudienceOptimizationJson,
  PrimaryGoalData,
  getDateString,
  MetricOptimizationJson,
  EventOptimizationJson,
  OptimizationVariation,
  BaseOptimizationVariation,
  OptimizationJsonSiteOptimizeAnalyze,
  CustomerPrimaryMetricType,
  CampaignIntervalWithOptimizationJson,
} from '@packages/systems/optimization/types';
import type {
  AudienceJson,
  CampaignInterval,
  CampaignJson,
  ConditionJson,
  CustomerJson,
  CustomerPrimaryMetrics,
  CustomerVertical,
  ExperienceJson,
  ExperienceType,
  MetricJson,
  VariationJson,
  VariationState,
} from '@intellimize/customer';
import {differenceInCalendarDays, parseISO} from 'date-fns';
import {RedirectOptimizationVariation} from '@packages/systems/optimize-analyze-admin';
import {partition} from 'lodash';
import {
  buildSyntheticMetric,
  getCampaignPrimaryMetricType,
  isAutoGoal,
  getMetricUiType,
} from '@packages/systems/optimization/utils';
import {isWebflowPlaybookId} from '../utils/isWebflowPlaybookId';
import {
  experienceTypeDisplayLabelMap,
  getCustomerPrimaryMetricIdSet,
  getExperienceStatus,
  getExperienceStatusLabel,
  getPrimaryMetrics,
  type CampaignPrimaryMetricType,
} from '@packages/domains/optimize/logic';

const NA_DASHES = '--';

const buildPrimaryGoal = ({
  primaryMetricIds,
  primaryMetricType,
  metrics,
  vertical,
  customerGoalLabel,
}: {
  primaryMetricIds: string[];
  primaryMetricType: CampaignPrimaryMetricType;
  metrics: MetricJson[];
  vertical?: CustomerVertical;
  customerGoalLabel?: string;
}): PrimaryGoalData => {
  const primaryMetrics = getPrimaryMetrics(new Set(primaryMetricIds), metrics);
  const syntheticMetric = buildSyntheticMetric({
    primaryMetricIds,
    primaryMetricType,
    metrics: primaryMetrics,
    vertical,
    customerGoalLabel,
  });
  const basePrimaryGoalData = {
    metricIds: [...primaryMetricIds],
    metricType: primaryMetricType,
  };
  if (syntheticMetric === undefined) {
    if (
      primaryMetricType === 'CAMPAIGN_ONE' ||
      primaryMetricType === 'CUSTOMER_ONE'
    ) {
      const [selectedMetric] = primaryMetrics;
      return {
        name: selectedMetric ? selectedMetric.name : 'NO NAME',
        ...basePrimaryGoalData,
      };
    }

    return {
      name: NA_DASHES,
      ...basePrimaryGoalData,
    };
  }

  return {
    name: syntheticMetric.name,
    ...basePrimaryGoalData,
  };
};

const dtfYMD = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
} as const;

const dtfMD = {
  month: 'short',
  day: 'numeric',
} as const;

const rtf = new Intl.RelativeTimeFormat();

const humanizeDate = (date: string | Date): string => {
  const now = new Date();
  const iso = typeof date === 'string' ? parseISO(date) : date;
  const days = differenceInCalendarDays(iso, now);

  const isYearDiff = now.getUTCFullYear() !== iso.getUTCFullYear();
  if (days === 0) {
    return 'Today';
  }

  if (days >= -30) {
    return rtf.format(days, 'days');
  }

  if (isYearDiff) {
    return getDateString(iso, dtfYMD);
  }

  return getDateString(iso, dtfMD);
};

const getVariationStateLabel = (
  state: VariationState,
  experienceType: ExperienceType
) => {
  return {
    live: 'Live',
    approving: 'Ready',
    draft: 'Draft',
    paused: experienceType === 'ab' ? 'Off' : 'Paused',
    archived: 'Archived',
    // Below status aren't used, but adding props for completeness
    deleted: 'Deleted',
    editing: 'Draft',
    reapproving: 'Draft',
  }[state];
};

const variationJsonToOptimizationVariation = (
  experienceType: ExperienceType,
  experienceId: string,
  variationId: string,
  variation: VariationJson,
  redirectPageData?: RedirectOptimizationVariation
): OptimizationVariation => {
  const {state, name, condition} = variation;

  const isBaseVariation =
    variation.type === undefined
      ? !variation.changes || variation.changes.length === 0
      : variation.type === 'nochange';

  const baseVariation: BaseOptimizationVariation = {
    id: variationId,
    experienceId,
    name,
    isBaseVariation,
    isRbpFallBack:
      experienceType === 'rbp' && variation.condition === undefined,
    state,
    condition,
    statusLabel: getVariationStateLabel(state, experienceType),
  };

  if (variation.type === 'wfrd') {
    return {
      ...baseVariation,
      isRedirectVariation: true,
      isMissingPageData: redirectPageData === undefined,
      redirect:
        redirectPageData !== undefined
          ? redirectPageData
          : {
              pageId: '',
              targetType: 'page',
              redirectTarget: {
                pageId: '',
              },
            },
    };
  }

  return {
    ...baseVariation,
    isRedirectVariation: false,
    isMissingPageData: false,
  };
};

export const toCampaignIntervalWithOptimizationJson = (
  intervals: CampaignInterval[] | undefined,
  events: CustomerJson['events']
): CampaignIntervalWithOptimizationJson[] | undefined =>
  intervals?.map((interval) => ({
    ...interval,
    metrics: interval.metrics
      ? interval.metrics?.map((metric) =>
          metricToOptimizationMetricJson(metric, events)
        )
      : [],
    primaryMetrics: interval.primaryMetrics
      ? interval.primaryMetrics.map((metric) =>
          metricToOptimizationMetricJson(metric, events)
        )
      : undefined,
  }));

export const experienceJsonToOptimizationExperienceJson = ({
  campaign,
  customerMetrics,
  optimizationMetrics,
  events,
  experience,
  experienceId,
  campaignId,
  vertical,
  goalLabel,
  experienceOptimizationRedirects,
}: {
  campaign: CampaignJson;
  customerMetrics: CustomerJson['metrics'];
  optimizationMetrics: CustomerJson['metrics'];
  events: CustomerJson['events'];
  experience: ExperienceJson;
  campaignId: string;
  experienceId: string;
  vertical: CustomerJson['vertical'] | undefined;
  goalLabel?: CustomerJson['goalLabel'];
  experienceOptimizationRedirects?: Record<
    string,
    RedirectOptimizationVariation
  >;
}): ExperienceOptimizationJson => {
  const optimizationMetricIds = optimizationMetrics.map(({id}) => id);
  const {name, type = 'cc', updatedAt} = experience;
  const {metrics, name: campaignName} = campaign;

  const primaryMetricType = getCampaignPrimaryMetricType(
    campaign.primaryMetrics
  );

  const experienceStatus = getExperienceStatus(experience);
  const primaryMetricIds =
    campaign.primaryMetrics.metricIds.length > 0
      ? campaign.primaryMetrics.metricIds
      : optimizationMetricIds;
  const primaryGoal = buildPrimaryGoal({
    vertical,
    primaryMetricIds,
    // pass all customer-level metrics to buildPrimaryGoal to allow for optimization with
    // primary metric(s) that are not referenced in root-level primaryMetric
    metrics: [...customerMetrics, ...campaign.metrics],
    primaryMetricType,
    customerGoalLabel: goalLabel,
  });
  const experienceTypeLabel = experienceTypeDisplayLabelMap[type || 'cc'];
  const statusLabel = getExperienceStatusLabel(
    experienceStatus,
    experience.type || 'cc'
  );
  const trafficAllocation =
    experience.type === 'ab' ? experience.trafficAllocation : {};

  const variationPriority =
    experience.type === 'rbp' ? experience.variationPriority : [];

  const playbookId =
    experience.playbookId && isWebflowPlaybookId(experience.playbookId)
      ? experience.playbookId
      : null;

  const userGuidance = experience.userGuidance ?? null;

  return {
    selectedCustomerMetricIds: campaign.selectedCustomerMetricIds ?? [],
    id: experienceId,
    campaignId,
    campaignName,
    name,
    expTypeLabel: experienceTypeLabel,
    updatedAt,
    updatedAtLabel: updatedAt !== undefined ? humanizeDate(updatedAt) : '--',
    primaryGoal,
    status: experienceStatus,
    statusLabel,
    playbookId,
    userGuidance,
    searchStringArray: [
      experienceId,
      name,
      primaryGoal.name,
      experienceTypeLabel,
      type,
    ],
    pageIds: experience.pageIds,
    metrics: moveAutoGoalToLast(
      metricsToOptimizationMetricJson(metrics, events)
    ),
    primaryMetricSynthetic: buildSyntheticMetric({
      primaryMetricIds,
      primaryMetricType,
      metrics: [...optimizationMetrics, ...metrics],
      vertical,
      customerGoalLabel: goalLabel,
    }),
    audienceIds: experience.audienceIds ?? [],
    modifiedIntervals: toCampaignIntervalWithOptimizationJson(
      campaign.modifiedIntervals,
      events
    ),
    condition: experience.condition,
    ...(type === 'ab'
      ? {
          type: 'ab',
          trafficAllocation,
        }
      : {type}),
    variationPriority,
    variations: Object.fromEntries(
      Object.entries(experience.variations).map(
        ([variationId, variationJson]) => [
          variationId,
          variationJsonToOptimizationVariation(
            experience.type ?? 'cc',
            experienceId,
            variationId,
            variationJson,
            experienceOptimizationRedirects?.[variationId]
          ),
        ]
      )
    ),
    disableChanges: experience.type === 'ab' && experienceStatus !== 'draft',
    disableStateChanges:
      experience.type === 'ab' && experienceStatus === 'not-live',
  };
};

const checkIsAudienceUsed = (
  condition: ConditionJson,
  audienceId: string
): {isInclude: boolean} | null => {
  if (condition.type === 'audience' && condition.audienceId === audienceId) {
    return {isInclude: true};
  } else if (condition.type === 'logic-and' || condition.type === 'logic-or') {
    for (const childCondition of condition.conditions) {
      const result = checkIsAudienceUsed(childCondition, audienceId);
      if (result) return result;
    }
  } else if (condition.type === 'logic-not') {
    const result = checkIsAudienceUsed(condition.condition, audienceId);
    if (result) {
      return {isInclude: false};
    }
  }
  return null;
};

const audienceJsonToOptimizationAudienceJson = (
  audienceId: string,
  audience: AudienceJson,
  experiences: ExperienceOptimizationJson[]
): AudienceOptimizationJson => {
  const associatedVariations: Record<
    string,
    {name: string; isInclude: boolean; experienceId: string}
  > = {};
  const associatedExperiences: Record<
    string,
    {name: string; isInclude: boolean}
  > = {};

  experiences.forEach((experience) => {
    if (experience.condition) {
      const result = checkIsAudienceUsed(experience.condition, audienceId);
      if (result) {
        associatedExperiences[experience.id] = {
          name: experience.name,
          isInclude: result.isInclude,
        };
      }
    }

    // this will be true if the audience is applied as a "limit audience" for an optimization
    if (experience.audienceIds.includes(audienceId)) {
      associatedExperiences[experience.id] = {
        name: experience.name,
        isInclude: true,
      };
    }

    Object.values(experience.variations).forEach((variation) => {
      if (variation.condition) {
        const result = checkIsAudienceUsed(variation.condition, audienceId);
        if (result) {
          associatedVariations[variation.id] = {
            name: variation.name,
            isInclude: result.isInclude,
            experienceId: experience.id,
          };
        }
      }
    });
  });

  return {
    ...audience,
    experiences: associatedExperiences,
    variations: associatedVariations,
  };
};

export const metricToOptimizationMetricJson = (
  metric: MetricJson,
  events: CustomerJson['events']
): MetricOptimizationJson => {
  const {eventIds} = metric;
  const eventsJson = eventIds.reduce<EventOptimizationJson[]>(
    (acc, eventId) => {
      const event = events[eventId];
      if (event !== undefined) {
        acc.push({
          id: eventId,
          name: event.name,
          ...(event.type === 'custom'
            ? {
                type: event.type,
                apiName: event.apiName,
                conversionType: event.conversionType || 'on-site',
              }
            : {type: event.type}),
        });
      }
      return acc;
    },
    []
  );
  return {
    ...metric,
    events: eventsJson,
    uiType: getMetricUiType(eventsJson),
  };
};

export const getCustomerPrimaryMetricType = (
  primaryMetrics?: CustomerPrimaryMetrics
): CustomerPrimaryMetricType => {
  if (primaryMetrics === undefined) {
    return 'CUSTOMER_NONE';
  }
  if (primaryMetrics.metricIds.length === 0) {
    return 'CUSTOMER_ALL';
  }

  return 'CUSTOMER_SOME';
};

// this util moves the AutoGoal goal to end of the optimization metrics array
// AutoGoal has at least one event of type 'wf_engagement_click'
export const moveAutoGoalToLast = (
  metrics: MetricOptimizationJson[]
): MetricOptimizationJson[] => {
  const [autoGoals, nonAutoGoals] = partition(metrics, isAutoGoal);
  return [...nonAutoGoals, ...autoGoals];
};

const metricsToOptimizationMetricJson = (
  metrics: MetricJson[],
  events: CustomerJson['events']
): MetricOptimizationJson[] => {
  return metrics.map((metric) =>
    metricToOptimizationMetricJson(metric, events)
  );
};

const eventsToUpdatedEventJson = ({
  events,
  viewEventsConfig,
}: {
  events: CustomerJson['events'];
  viewEventsConfig:
    | OptimizationJsonSiteOptimizeAnalyze['viewEvents']
    | undefined;
}): OptimizationJson['events'] => {
  return Object.fromEntries(
    Object.entries(events ?? {}).map(([eventId, eventJson]) => {
      if (eventJson.type === 'wf_view') {
        const eventConfig = viewEventsConfig?.[eventId];
        return [
          eventId,
          {
            ...eventJson,
            ...(eventConfig ? {config: eventConfig} : {}),
          },
        ];
      }
      return [eventId, eventJson];
    })
  );
};

export const customerJsonToOptimizationJson = (
  customer: CustomerJson,
  siteOptimizeAnalyzeData?: OptimizationJsonSiteOptimizeAnalyze
): OptimizationJson => {
  const {metrics, pages, vertical} = customer;
  const experiences: OptimizationJson['experiences'] = {};

  const primaryMetricsIdSet = getCustomerPrimaryMetricIdSet(
    customer.primaryMetrics,
    customer.metrics
  );

  const optimizationMetrics = getPrimaryMetrics(
    primaryMetricsIdSet,
    customer.metrics
  );

  const transformedEvents = eventsToUpdatedEventJson({
    events: customer.events,
    viewEventsConfig: siteOptimizeAnalyzeData?.viewEvents,
  });

  const partitionedMetrics = partitionMetricsByPrimary(
    metrics,
    primaryMetricsIdSet
  );

  Object.entries(customer.campaigns).forEach(([campaignId, campaign]) => {
    Object.entries(campaign.experiences).forEach(
      ([experienceId, experience]) => {
        experiences[experienceId] = experienceJsonToOptimizationExperienceJson({
          customerMetrics: metrics,
          optimizationMetrics,
          campaign,
          events: eventsToUpdatedEventJson({
            events: customer.events,
            viewEventsConfig: siteOptimizeAnalyzeData?.viewEvents,
          }),
          experience,
          campaignId,
          experienceId,
          vertical: customer.vertical,
          goalLabel: customer.goalLabel,
          experienceOptimizationRedirects:
            siteOptimizeAnalyzeData?.redirectOptimizations?.[experienceId]
              ?.variations,
        });
      }
    );
  });

  const audiences: OptimizationJson['audiences'] = {};
  Object.entries(customer.audiences).forEach(([audienceId, audience]) => {
    audiences[audienceId] = audienceJsonToOptimizationAudienceJson(
      audienceId,
      audience,
      Object.values(experiences)
    );
  });

  return {
    id: customer._id,
    name: customer.name,
    version: customer.__v,
    events: transformedEvents,
    experiences,
    pages,
    audiences,
    metrics: metricsToOptimizationMetricJson(
      partitionedMetrics,
      customer.events
    ),
    totalMetrics: buildSyntheticMetric({
      primaryMetricIds: [...primaryMetricsIdSet],
      primaryMetricType: 'CUSTOMER_ALL',
      metrics: optimizationMetrics,
      vertical,
      customerGoalLabel: customer.goalLabel,
    }),
    vertical,
    goalLabel: customer.goalLabel,
    iint: customer.iint,
    urlParamFilters: customer.urlParamFilters,
    customAttributeFilters: customer.customAttributeFilters,
    engagementClickEventId: Object.keys(customer.events).find(
      (eventId) => customer.events[eventId]?.type === 'wf_engagement_click'
    ),
    limitedModeCondition: customer.limitedModeCondition,
    primaryMetricsIdSet,
    primaryMetricType: getCustomerPrimaryMetricType(customer.primaryMetrics),
    optimizationMetrics: metricsToOptimizationMetricJson(
      optimizationMetrics,
      customer.events
    ),
    origins: customer.origins ?? [],
    hasSiteOptimizationRedirectError:
      siteOptimizeAnalyzeData?.redirectOptimizations === undefined,
  };
};

export const partitionMetricsByPrimary = (
  metrics: MetricJson[],
  primaryMetricsIdSet: Set<string>
): MetricJson[] => {
  const primary: MetricJson[] = [];
  const secondary: MetricJson[] = [];

  for (const metric of metrics) {
    if (primaryMetricsIdSet.has(metric.id)) {
      primary.push(metric);
    } else {
      secondary.push(metric);
    }
  }

  return [...primary, ...secondary];
};
